<template>
  <mu-card id="cholesterol-input">
    <mu-card-text id="cl-moving-bg" class="card-style" v-bind:class="{cardHasData: hasData}">
      <div v-if="loading">
        <mu-flexbox class="card-loading-padding" align="center" justify="center">
          <mu-flexbox-item align="center">
            <mu-circular-progress :size="56" color="#FFFFFF" :strokeWidth="5"/>
          </mu-flexbox-item>
        </mu-flexbox>
      </div>
      <div v-if="!loading" class="animated fadeIn">
        <mu-flexbox v-if="data.no_setting || data.error" class="card-error-padding" justify="center">
          <mu-flexbox-item align="center">
            <div class="card-notice-title" v-text="$t('message.cardEmpty')"></div>
            <div>{{ $t('message.cardTapPrefix') }}
              <mu-icon v-if="data.no_setting" class="card-icon-error" :size="16" slot="left" value="settings"/>
              <mu-icon v-if="data.error" class="card-icon-error" :size="16" slot="left" value="add"/>
              <span v-if="data.error || data.no_setting">{{ $t('message.cardTapSuffix') }}</span>
            </div>
          </mu-flexbox-item>
        </mu-flexbox>
        <div v-else @click="goToDetail" style="cursor: pointer">
          <span v-show="false">{{hasData = true}}</span>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                {{ $t('message.cardCholesterolLatestTotal') }}
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div class="card-large-stat">
                  <span v-text="data.total + ' ' + data.measure"></span>
                </div>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                {{ $t('message.cardCholesterolLatestHDL') }}
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div v-text="data.hdl + ' ' + blood_glucose.unit"/>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                {{ $t('message.cardCholesterolLatestLDL') }}
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div v-text="data.ldl + ' ' + blood_glucose.unit"/>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                {{ $t('message.cardCholesterolLatestTriglycerides') }}
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div v-text="data.triglycerides + ' ' + blood_glucose.unit"/>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
        </div>
      </div>
    </mu-card-text>
    <mu-card-actions>
      <mu-flexbox justify="flex_start" style="height: 48px">
        <svg class="card-left-icon" width="16px" height="14px" viewBox="20 193 16 14" version="1.1"
             xmlns="http://www.w3.org/2000/svg">
          <path
            d="M36,197.265908 C36,194.909874 34.0654937,193 31.6791924,193 C30.1209817,193 28.7601736,193.817042 27.9999513,195.038086 C27.2398264,193.817042 25.8784338,193 24.3202556,193 C21.9339542,193 20,194.909874 20,197.265908 C20,198.548637 20.5757115,199.696228 21.4830684,200.477715 L27.6056657,206.522912 C27.7102283,206.626148 27.8520374,206.684147 27.9999513,206.684147 C28.1478652,206.684147 28.2896742,206.626148 28.3942369,206.522912 L34.5168341,200.477715 C35.4242235,199.696228 36,198.548637 36,197.265908 L36,197.265908 Z"
            id="Shape" stroke="none" fill="#D14905" fill-rule="evenodd"></path>
        </svg>
        <mu-flexbox-item class="left16" v-text="loading ? $t('message.loadingText') : title"></mu-flexbox-item>
        <mu-flexbox-item align="right" v-if="data.no_setting" v-bind:class="{invisible: isOpen || loading}">
          <mu-icon-button
            :disabled="loading"
            class="card-bar-icon"
            icon="settings"
            @click.native="open"
          ></mu-icon-button>
        </mu-flexbox-item>
        <mu-flexbox-item
          align="right"
          v-if="!data.no_setting"
        >
          <mu-icon-button
            :disabled="loading"
            v-if="isOpenInput"
            class="card-bar-icon"
            :icon="'remove'"
            @click.native="openInput"
          />
          <mu-icon-button
            :disabled="loading"
            v-tooltip.top="$t('message.addEntryText')"
            v-if="!isOpenInput"
            class="card-bar-icon"
            :icon="'add'"
            @click.native="openInput"
          />
        </mu-flexbox-item>
      </mu-flexbox>
    </mu-card-actions>

    <transition :css="false" @enter="onToggleEnter" @leave="onToggleLeave">
      <form @submit.prevent="saveReading" v-if="isOpenInput" novalidate v-bind:style="{height: computedAuto}"
            style="overflow: hidden">
        <mu-card-text>
          <!-- First row -->
          <mu-row v-if="blood_glucose.measure == 'mmoll'">
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="12.95"
                hintText="Enter mmol/L"
                :data-vv-rules="'required|min_value:0|max_value:12.95'"
                data-vv-name="total_mmoll"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolTotal')"
                :errorText="errors.first('total_mmoll')"
                v-model="model.total_mmoll"
                :label="$t('message.labelCholesterolTotal')"
                helpText="mmol/L"
                :fullWidth="true"/>
            </mu-col>
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="12.95"
                hintText="Enter mmol/L"
                :data-vv-rules="'min_value:0|max_value: 12.95'"
                data-vv-name="hdl_mmoll"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolHDL')"
                :errorText="errors.first('hdl_mmoll')"
                v-model="model.hdl_mmoll"
                :label="$t('message.labelCholesterolHDL')"
                helpText="mmol/L"
                :fullWidth="true"/>
            </mu-col>
          </mu-row>
          <mu-row v-if="blood_glucose.measure == 'mgdl'">
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="700"
                hintText="Enter mg/dL"
                :data-vv-rules="'required|min_value:0|max_value:700'"
                data-vv-name="total_mgdl"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolTotal')"
                :errorText="errors.first('total_mgdl')"
                v-model="model.total_mgdl"
                :label="$t('message.labelCholesterolTotal')"
                :fullWidth="true"
                helpText="mg/dL"/>
            </mu-col>
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="700"
                hintText="Enter mg/dL"
                :data-vv-rules="'min_value:0|max_value:700'"
                data-vv-name="hdl_mgdl"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolHDL')"
                :errorText="errors.first('hdl_mgdl')"
                v-model="model.hdl_mgdl"
                :label="$t('message.labelCholesterolHDL')"
                :fullWidth="true"
                helpText="mg/dL"/>
            </mu-col>
          </mu-row>
          <!-- End first row -->

          <!-- Second row -->
          <mu-row v-if="blood_glucose.measure == 'mmoll'" class="bottom">
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="18.1"
                hintText="mmol/L"
                :data-vv-rules="'min_value:0|max_value:18.1'"
                data-vv-name="triglycerides_mmoll"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolTriglycerides')"
                :errorText="errors.first('triglycerides_mmoll')"
                v-model.number="model.triglycerides_mmoll"
                :label="$t('message.labelCholesterolTriglycerides')"
                :fullWidth="true"
                helpText="mmol/L"/>
            </mu-col>
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="18.1"
                hintText="mmol/L"
                :data-vv-rules="'min_value:0|max_value: 18.1'"
                data-vv-name="ldl_mmoll"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolLDL')"
                :errorText="errors.first('ldl_mmoll')"
                v-model.number="model.ldl_mmoll"
                :label="$t('message.labelCholesterolLDL')"
                :fullWidth="true"
                :helpText="blood_glucose.unit"/>
            </mu-col>
          </mu-row>
          <mu-row v-if="blood_glucose.measure == 'mgdl'" class="bottom">
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="700"
                hintText="mg/dL"
                :data-vv-rules="'min_value:0|max_value: 700'"
                data-vv-name="triglycerides_mgdl"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolTriglycerides')"
                :errorText="errors.first('triglycerides_mgdl')"
                v-model.number="model.triglycerides_mgdl"
                :label="$t('message.labelCholesterolTriglycerides')"
                :fullWidth="true"
                :helpText="blood_glucose.unit"/>
            </mu-col>
            <mu-col width="100" desktop="50" tablet="50">
              <mu-text-field
                type="number"
                v-validate
                min="0"
                max="700"
                hintText="mg\dL"
                :data-vv-rules="'min_value:0|max_value:700'"
                data-vv-name="ldl_mgdl"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.labelCholesterolLDL')"
                :errorText="errors.first('ldl_mgdl')"
                v-model.number="model.ldl_mgdl"
                :label="$t('message.labelCholesterolLDL')"
                :fullWidth="true"
                :helpText="blood_glucose.unit"/>
            </mu-col>
          </mu-row>
          <!-- End second row -->


          <mu-checkbox v-model="model.customDate" :label="$t('message.labelCustomDate')"></mu-checkbox>
          <date-picker v-if="model.customDate" type="datetime" v-model="model.date"></date-picker>

        </mu-card-text>
        <mu-card-actions align="right">
          <button type="submit" style="display: none"></button>
          <mu-flat-button
            :label="$t('message.discardText')"
            @click.native="closeInput"
            class="demo-flat-button"
          />
          <mu-flat-button
            @click.native="saveReading()"
            type="button"
            :disabled="isSaving"
            :label="isSaving ? $t('message.savingText'): $t('message.saveText')"
            class="demo-flat-button"
          />
        </mu-card-actions>
      </form>
    </transition>

  </mu-card>
</template>
<script>

  import {TweenMax, TweenLite, Power3, ScrollToPlugin} from 'gsap/all'
  import 'gsap/ScrollToPlugin'

  const plugins = [ScrollToPlugin];

  import config from '../../../../config'
  import CholesterolApi from '../../../../api/Tracking/Cholesterol'
  import ScrollerMixin from '../../../../mixins/ScrollerMixin'
  import CardAnimationMixin from '../../../../mixins/CardAnimationMixin'
  import DatePicker from "../../../DateTimePicker/DateTimePicker.vue";

  export default {
    components: {DatePicker},
    mixins: [ScrollerMixin, CardAnimationMixin],
    name: 'cholesterol-input',
    props: {
      blood_glucose: {},
    },
    data() {
      return {
        title: this.$t('message.cardCholesterol'),
        isOpen: false,
        isOpenInput: false,
        isSaving: false,
        isBackgroundShown: true,
        hasData: false,
        noData: false,
        loading: true,
        data: {},
        value: [0, 5],
        settings: {
          blood_glucose: 0
        },
        readingTags: [],
        model: {
          tags: []
        }
      }
    },
    mounted() {
      let self = this;

      self.loadData();

      self.$EventBus.$on('cholesterolPusherReceived', function (model) {
        self.$nextTick(function () {
          self.data = model;
          self.$forceUpdate();
        });
      });

      self.$EventBus.$on('cholesterolReadingUpdated', function () {
        self.$store.commit('setCholesterolCard', {});
        self.loadData();
      });

      self.$EventBus.$on('lc:settingsUpdated', function () {
        self.loadData();
      });
    },
    methods: {
      formatDate(date) {
        return config.dateFormatting.fromNow(date)
      },
      saveReading() {
        let self = this;
        self.isSaving = true;
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid) {


              if (!_.has(self.model, 'date')) {
                self.model.date = moment().format();
              }

              self.isSaving = true;
              let postData = CholesterolApi.postCholesterolTracking(self.model);
              postData.then(function (response) {
                self.$store.commit('setCholesterolCard', {});
                self.$gtag.event( 'ReadingAdded',{
                  'event_category': 'CholesterolCard',
                  'event_label': 'Added reading',
                  'value': 1
                });
                self.$toastr.success(self.$t('message.cholesterolReadingAdded'));
                if (self.$route.name === 'Dashboard') {
                  TweenLite.to(self.scroller, 0.2, {
                    scrollTo: {
                      y: self.$el,
                      offsetY: 16
                    }
                  })
                } else {
                  self.$EventBus.$emit('updateGraph');
                  self.$EventBus.$emit('cholesterolReadingAdded');
                }
                self.model = {};
                self.isSaving = false;
                self.loadData()
              })
            } else {
              self.isSaving = false;
            }
          })
          .catch(function () {
            self.isSaving = false;
          })
      },
      loadData() {
        let self = this;
        self.loading = true;
        self.isSaving = false;
        self.isOpen = false;
        self.isOpenInput = false;

        if (_.has(self.$store.state.cards.cholesterol, 'measure')) {
          self.loading = false;
          self.data = self.$store.state.cards.cholesterol;
        } else {
          let getData = CholesterolApi.getCholesterolTrackingCard();
          getData.then(function (response) {
              const status = response.status;
              if (status === 200) {
                let data = response.data;
                self.loading = false;
                self.data = data;
                self.$store.commit('setCholesterolCard', response.data);
              } else {
                if (status === 201) {
                  self.data = response.data;
                  self.loading = false
                }
              }
            }
          )
        }

      },
      goToDetail() {
        let self = this;
        self.$router.push({
          name: 'CholesterolProgress'
        })
      },
      open() {
        this.isOpen = true
      },
      close() {
        this.isOpen = false
      },
      openInput() {
        let self = this;
        self.isOpenInput = !self.isOpenInput;

        if (self.isOpenInput) {
          self.$gtag.event('Open',{
            'event_category': 'CholesterolCard',
            'event_label': 'Opened card',
            'value': 0
          })
        }
        if (!self.isOpenInput) {
          self.$gtag.event('Closed',{
            'event_category': 'CholesterolCard',
            'event_label': 'Closed card',
            'value': 0
          })
        }

      },
      closeInput() {
        let self = this;
        self.model = {
          tags: []
        };
        self.isOpenInput = false;
        self.$gtag.event('Closed',{
          'event_category': 'CholesterolCard',
          'event_label': 'Closed card',
          'value': 0
        })

      }
    },
    beforeDestroy() {
      this.$EventBus.$off('cholesterolReadingUpdated')
      this.$EventBus.$off('cholesterolPusherReceived');
    }
  }
</script>
<style lang="less">
  @import "cholesterol.less";
</style>



// WEBPACK FOOTER //
// Cholesterol.vue?6d22f08c